import * as maplibregl from 'maplibre-gl';
import type { Feature } from 'geojson';
import type { Popup } from 'maplibre-gl';

import { Level, Level_t } from '@wemap/geo';
import type { WemapMap } from '@wemap/map';
import { EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from './geoJsonSource';

const GEOJSON_SOURCE = 'geojson';

type FeatureWithLevel = {
    feature: Feature,
    level: Level_t
};


export default class MapManager {

    private featuresWithLevel: FeatureWithLevel[] = [];
    private clickPopup: Popup | null = null;
    private unsubscribeLevelChange: (() => void) | null = null;

    constructor(private wemapMap: WemapMap) {
        this.initialize();
    }

    async addGeoJSON(geojson: GeoJSON.FeatureCollection) {
        this.featuresWithLevel = geojson.features.map(f => ({
            feature: f,
            level: this.parseLevelValue(f.properties?.level)
        }));

        const level = this.wemapMap.getLevel();

        if (level !== null) {
            this.updateFeatures(level);
        }
    }

    private parseLevelValue(level: any): Level_t {
        if (level === undefined || level === null) return null;

        if (typeof level === 'string') {
            return Level.fromString(level);
        }

        if (typeof level === 'number') {
            return level;
        }

        return null;
    }

    async initialize() {

        const map = this.wemapMap.maplibre;

        this.wemapMap.addSource(GEOJSON_SOURCE, {
            type: 'geojson',
            data: EMPTY_FEATURE_COLLECTION,
        });

        this.wemapMap.addLayer({
            id: 'markers',
            type: 'circle',
            source: GEOJSON_SOURCE,
            filter: ['==', ['geometry-type'], 'Point'],
            paint: {
                'circle-color': ['coalesce', ['get', 'marker-color'], '#000000'],
                'circle-radius': [
                    'match',
                    ['coalesce', ['get', 'marker-size'], 'medium'],
                    'small', 3,
                    'medium', 5,
                    'large', 7,
                    5 // Default size for 'medium'
                ]
            }
        });

        this.wemapMap.addLayer({
            id: 'lines',
            type: 'line',
            source: GEOJSON_SOURCE,
            filter: ['==', ['geometry-type'], 'LineString'],
            paint: {
                'line-color': ['coalesce', ['get', 'stroke'], 'blue'],
                'line-width': ['coalesce', ['get', 'stroke-width'], 2],
                'line-opacity': ['coalesce', ['get', 'stroke-opacity'], 1]
            }
        });

        this.wemapMap.addLayer({
            id: 'polygons',
            type: 'fill',
            source: GEOJSON_SOURCE,
            filter: ['==', ['geometry-type'], 'Polygon'],
            paint: {
                'fill-color': ['coalesce', ['get', 'fill'], '#555555'],
                'fill-opacity': ['coalesce', ['get', 'fill-opacity'], 0.5]
            }
        });

        // Add click handler for features
        map.on('click', (e) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: ['markers', 'lines', 'polygons']
            });

            if (features.length > 0) {
                const feature = features[0];

                // Remove existing popup if any
                this.clickPopup?.remove();

                // Create popup content
                const popupNode = document.createElement('div');
                const properties = feature.properties || {};

                // Format properties as HTML
                const content = Object.entries(properties)
                    .map(([key, value]) => `<strong>${key}:</strong> ${value}`)
                    .join('<br/>');

                popupNode.innerHTML = content;

                // Create and show popup
                this.clickPopup = new maplibregl.Popup()
                    .setLngLat(e.lngLat)
                    .setDOMContent(popupNode)
                    .addTo(map);
            }
        });

        // Change cursor to pointer when hovering features
        ['markers', 'lines', 'polygons'].forEach(layer => {
            map.on('mouseenter', layer, () => {
                map.getCanvas().style.cursor = 'pointer';
            });

            map.on('mouseleave', layer, () => {
                map.getCanvas().style.cursor = '';
            });
        });

        this.unsubscribeLevelChange = this.wemapMap.onLevelChange((level) => {
            if (level !== null) {
                this.updateFeatures(level);
            }
        });

        const initialLevel = this.wemapMap.getLevel();

        if (initialLevel !== null) {
            this.updateFeatures(initialLevel);
        }
    }

    destroy() {
        this.unsubscribeLevelChange?.();
        this.clickPopup?.remove();
        this.wemapMap.removeLayer('markers');
        this.wemapMap.removeLayer('lines');
        this.wemapMap.removeLayer('polygons');
        this.wemapMap.removeSource(GEOJSON_SOURCE);
    }

    updateFeatures(level: number) {
        if (!this.wemapMap.maplibre.style) {
            return;
        }

        setGeoJsonSourceData(this.wemapMap.maplibre, GEOJSON_SOURCE, {
            type: 'FeatureCollection',
            features: this.featuresWithLevel
                .filter(f => Level.intersect(f.level, level))
                .map(f => f.feature)
        });
    }
}
